import React, { useState } from "react";
import { formatCurrency } from "../../../../utils/formatters";
import { motion, AnimatePresence } from "framer-motion";

// Add filing status options
const FILING_STATUSES = [
  { value: "single", label: "Single" },
  { value: "married_joint", label: "Married Filing Jointly" },
  { value: "married_separate", label: "Married Filing Separately" },
  { value: "head_household", label: "Head of Household" },
];

// Add US states array at the top with the other constants
const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

const HouseholdInfoForm = ({ data, onChange }) => {
  const [showCustomType, setShowCustomType] = useState({
    firstMember: data.firstMember.incomeSources.map(
      (source) => source.type === "Other",
    ),
    secondMember: data.secondMember.incomeSources.map(
      (source) => source.type === "Other",
    ),
  });

  const handleMemberChange = (member, field, value) => {
    const updatedData = { ...data };
    if (field.includes(".")) {
      const [category, subfield] = field.split(".");
      updatedData[member][category][subfield] = value;
    } else {
      updatedData[member][field] = value;
    }
    onChange(updatedData);
  };

  const handleAddIncomeSource = (member) => {
    const updatedData = { ...data };
    if (!updatedData[member].incomeSources) {
      updatedData[member].incomeSources = [];
    }
    updatedData[member].incomeSources.push({
      type: "",
      incomeForm: "",
      grossAmount: "",
      startDate: "",
      isCurrent: false,
      isLifetime: false,
      endDate: "",
      deductions: [],
      otherType: "",
    });
    onChange(updatedData);
  };

  const handleAddDeduction = (member, incomeIndex) => {
    const updatedData = { ...data };
    updatedData[member].incomeSources[incomeIndex].deductions.push({
      type: "federal_tax",
      amount: "",
    });
    onChange(updatedData);
  };

  const handleIncomeSourceChange = (member, index, field, value) => {
    const updatedData = { ...data };
    updatedData[member].incomeSources[index][field] = value;
    onChange(updatedData);
  };

  const handleDeductionChange = (
    memberIndex,
    sourceIndex,
    deductionIndex,
    field,
    value,
  ) => {
    const newData = { ...data };
    const member = memberIndex === 0 ? "firstMember" : "secondMember";
    newData[member].incomeSources[sourceIndex].deductions[deductionIndex][
      field
    ] = value;
    onChange(newData);
  };

  const handleRemoveIncomeSource = (member, index) => {
    const updatedData = { ...data };
    updatedData[member].incomeSources.splice(index, 1);
    onChange(updatedData);
  };

  const handleRemoveDeduction = (member, incomeIndex, deductionIndex) => {
    const updatedData = { ...data };
    updatedData[member].incomeSources[incomeIndex].deductions.splice(
      deductionIndex,
      1,
    );
    onChange(updatedData);
  };

  const handleIncomeTypeChange = (member, index, value) => {
    const newIncomeSources = [...data[member].incomeSources];
    if (value === "Other") {
      newIncomeSources[index] = {
        ...newIncomeSources[index],
        type: value,
        customType: "",
      };

      setShowCustomType((prev) => ({
        ...prev,
        [member]: prev[member].map((show, i) => (i === index ? true : show)),
      }));
    } else {
      newIncomeSources[index] = {
        ...newIncomeSources[index],
        type: value,
        customType: undefined,
      };
    }
    handleMemberChange(member, "incomeSources", newIncomeSources);
  };

  const handleCustomTypeChange = (member, index, value) => {
    const newIncomeSources = [...data[member].incomeSources];
    newIncomeSources[index] = {
      ...newIncomeSources[index],
      customType: value,
    };

    if (!value) {
      newIncomeSources[index].type = "";
      setShowCustomType((prev) => ({
        ...prev,
        [member]: prev[member].map((show, i) => (i === index ? false : show)),
      }));
    }

    handleMemberChange(member, "incomeSources", newIncomeSources);
  };

  // Helper to check if filing status includes spouse
  const isMarriedStatus = (status) => {
    return status === "married_joint" || status === "married_separate";
  };

  const renderIncomeSourceFields = (member, source, index) => (
    <div className="grid grid-cols-3 gap-4 mb-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Income Type
        </label>
        {!showCustomType[member][index] ? (
          <select
            value={source.type}
            onChange={(e) =>
              handleIncomeTypeChange(member, index, e.target.value)
            }
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
          >
            <option value="">Select Type</option>
            <option value="salary">Salary</option>
            <option value="hourly">Hourly Wages</option>
            <option value="commission">Commission</option>
            <option value="bonus">Bonus</option>
            <option value="pension">Pension</option>
            <option value="social_security">Social Security</option>
            <option value="investment">Investment Income</option>
            <option value="rental">Rental Income</option>
            <option value="business">Business Income</option>
            <option value="Other">Other</option>
          </select>
        ) : (
          <input
            type="text"
            value={source.customType || ""}
            onChange={(e) =>
              handleCustomTypeChange(member, index, e.target.value)
            }
            onBlur={(e) => {
              if (!e.target.value.trim()) {
                const newIncomeSources = [...data[member].incomeSources];
                newIncomeSources[index] = {
                  ...newIncomeSources[index],
                  type: "",
                  customType: undefined,
                };
                setShowCustomType((prev) => ({
                  ...prev,
                  [member]: prev[member].map((i) => (i === index ? false : i)),
                }));
                handleMemberChange(member, "incomeSources", newIncomeSources);
              }
            }}
            placeholder="Enter Income Type"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
          />
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Gross Amount
        </label>
        <div className="mt-1 relative rounded-md shadow-sm">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500 dark:text-gray-500 sm:text-sm">$</span>
          </div>
          <input
            type="number"
            value={source.grossAmount}
            onChange={(e) =>
              handleIncomeSourceChange(
                member,
                index,
                "grossAmount",
                e.target.value,
              )
            }
            className="mt-1 block w-full pl-7 rounded-md border-gray-300 shadow-sm"
            placeholder="0.00"
          />
        </div>
      </div>

      <div className="flex flex-col">
        <label className="flex items-center mb-2">
          <input
            type="checkbox"
            checked={source.isCurrent || false}
            onChange={(e) => {
              handleIncomeSourceChange(
                member,
                index,
                "isCurrent",
                e.target.checked,
              );
              if (e.target.checked) {
                handleIncomeSourceChange(
                  member,
                  index,
                  "startDate",
                  new Date().toISOString().split("T")[0],
                );
              }
            }}
            className="mr-2"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Current Income</span>
        </label>

        {!source.isCurrent && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Start Date
            </label>
            <input
              type="date"
              value={source.startDate || ""}
              onChange={(e) =>
                handleIncomeSourceChange(
                  member,
                  index,
                  "startDate",
                  e.target.value,
                )
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
        )}
      </div>

      <div className="col-span-3">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={source.isLifetime || false}
            onChange={(e) => {
              handleIncomeSourceChange(
                member,
                index,
                "isLifetime",
                e.target.checked,
              );
              if (e.target.checked) {
                handleIncomeSourceChange(member, index, "endDate", "");
              }
            }}
            className="mr-2"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Income continues through life expectancy
          </span>
        </label>
      </div>

      {!source.isLifetime && (
        <div className="col-span-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            End Date
          </label>
          <input
            type="date"
            value={source.endDate || ""}
            onChange={(e) =>
              handleIncomeSourceChange(member, index, "endDate", e.target.value)
            }
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
          />
        </div>
      )}
    </div>
  );

  const getDeductionOptions = (sourceType) => {
    const commonDeductions = [
      { value: "federal_tax", label: "Federal Tax" },
      { value: "state_tax", label: "State Tax" },
      { value: "life_insurance", label: "Life Insurance" },
      { value: "dental", label: "Dental" },
      { value: "vision", label: "Vision" },
      { value: "health_insurance", label: "Health Insurance" },
      { value: "401k", label: "401(k)" },
      { value: "medicare_tax", label: "Medicare Tax" },
      { value: "social_security_tax", label: "Social Security Tax" },
      { value: "retire_8", label: "Retire 8" },
      { value: "union_dues", label: "Union Dues" },
      { value: "medicare_premium", label: "Medicare Premium" },
      { value: "other", label: "Other" },
    ];

    switch (sourceType) {
      case "social_security":
        return [
          { value: "federal_tax", label: "Federal Tax" },
          { value: "medicare_premium", label: "Medicare Premium" },
          { value: "other", label: "Other" },
        ];
      default:
        return commonDeductions;
    }
  };

  const renderDeductionInputs = (
    memberIndex,
    sourceIndex,
    deduction,
    deductionIndex,
  ) => {
    const source =
      memberIndex === 0
        ? data.firstMember.incomeSources[sourceIndex]
        : data.secondMember.incomeSources[sourceIndex];

    return (
      <div
        key={deductionIndex}
        className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4 p-4 bg-gray-50 rounded"
      >
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Type
          </label>
          <select
            value={deduction.type}
            onChange={(e) => {
              handleDeductionChange(
                memberIndex,
                sourceIndex,
                deductionIndex,
                "type",
                e.target.value,
              );
              if (e.target.value === "other" && !deduction.customType) {
                handleDeductionChange(
                  memberIndex,
                  sourceIndex,
                  deductionIndex,
                  "customType",
                  "",
                );
              }
            }}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
          >
            {getDeductionOptions(source.type).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {deduction.type === "other" && (
            <input
              type="text"
              value={deduction.customType || ""}
              onChange={(e) =>
                handleDeductionChange(
                  memberIndex,
                  sourceIndex,
                  deductionIndex,
                  "customType",
                  e.target.value,
                )
              }
              className="mt-2 block w-full rounded-md border-gray-300 shadow-sm"
              placeholder="Enter deduction type"
            />
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Amount
          </label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 dark:text-gray-500 sm:text-sm">$</span>
            </div>
            <input
              type="number"
              value={deduction.amount || ""}
              onChange={(e) =>
                handleDeductionChange(
                  memberIndex,
                  sourceIndex,
                  deductionIndex,
                  "amount",
                  e.target.value,
                )
              }
              className="mt-1 block w-full pl-7 rounded-md border-gray-300 shadow-sm"
              placeholder="0.00"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Start Date
          </label>
          <input
            type="date"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            value={deduction.startDate || source.startDate || ""}
            onChange={(e) =>
              handleDeductionChange(
                memberIndex,
                sourceIndex,
                deductionIndex,
                "startDate",
                e.target.value,
              )
            }
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            End Date
          </label>
          <input
            type="date"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            value={deduction.endDate || source.endDate || ""}
            onChange={(e) =>
              handleDeductionChange(
                memberIndex,
                sourceIndex,
                deductionIndex,
                "endDate",
                e.target.value,
              )
            }
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={() => {
              const newData = { ...data };
              const member = memberIndex === 0 ? "firstMember" : "secondMember";
              newData[member].incomeSources[sourceIndex].deductions.splice(
                deductionIndex,
                1,
              );
              onChange(newData);
            }}
            className="text-red-600 hover:text-red-800"
          >
            Remove
          </button>
        </div>
      </div>
    );
  };

  const renderIncomeSource = (memberIndex, source, sourceIndex) => {
    return (
      <div key={sourceIndex} className="border p-4 rounded mb-4">
        {renderIncomeSourceFields(
          memberIndex === 0 ? "firstMember" : "secondMember",
          source,
          sourceIndex,
        )}

        <div className="mt-4">
          <h4 className="text-lg font-medium mb-2">Deductions</h4>
          {(source.deductions || []).map((deduction, deductionIndex) =>
            renderDeductionInputs(
              memberIndex,
              sourceIndex,
              deduction,
              deductionIndex,
            ),
          )}
          <button
            type="button"
            className="mt-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-surface-default dark:bg-surface-default hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={() => {
              const newData = { ...data };
              const member = memberIndex === 0 ? "firstMember" : "secondMember";
              if (!newData[member].incomeSources[sourceIndex].deductions) {
                newData[member].incomeSources[sourceIndex].deductions = [];
              }
              newData[member].incomeSources[sourceIndex].deductions.push({
                type: "federal_tax",
                amount: "",
                startDate: source.startDate || "",
                endDate: source.endDate || "",
              });
              onChange(newData);
            }}
          >
            Add Deduction
          </button>
        </div>
      </div>
    );
  };

  const renderMemberSection = (member, title) => (
    <div className="mb-6">
      <h3 className="text-lg font-medium mb-4">{title}</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block mb-2">Name</label>
          <input
            type="text"
            className="w-full p-2 border rounded"
            value={data[member].name}
            onChange={(e) => handleMemberChange(member, "name", e.target.value)}
          />
        </div>
        <div>
          <label className="block mb-2">Date of Birth</label>
          <input
            type="date"
            className="w-full p-2 border rounded"
            value={data[member].dateOfBirth}
            onChange={(e) =>
              handleMemberChange(member, "dateOfBirth", e.target.value)
            }
          />
        </div>
      </div>

      <div className="mt-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-md font-medium">Income Sources</h4>
          <button
            onClick={() => handleAddIncomeSource(member)}
            className="inline-flex items-center px-4 py-2 rounded-full border border-transparent shadow-sm text-sm font-medium text-white bg-[#131d80] hover:bg-[#0f1660]"
          >
            <svg
              className="w-5 h-5 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Add Income Source
          </button>
        </div>

        <AnimatePresence>
          {data[member].incomeSources?.map((source, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-surface-default dark:bg-surface-default rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 mb-4"
            >
              {renderIncomeSource(
                member === "firstMember" ? 0 : 1,
                source,
                index,
              )}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => handleRemoveIncomeSource(member, index)}
                  className="text-red-600 hover:text-red-800"
                >
                  Remove Income Source
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );

  return (
    <div className="border rounded-lg p-6">
      <h2 className="text-xl font-semibold mb-4">Household Information</h2>

      {renderMemberSection("firstMember", "Primary Member")}

      <div className="mb-6 grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Filing Status
          </label>
          <select
            value={data.filingStatus}
            onChange={(e) => {
              const newStatus = e.target.value;
              onChange({
                ...data,
                filingStatus: newStatus,
                isMarried: isMarriedStatus(newStatus),
              });
            }}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
          >
            {FILING_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Tax Filing State
          </label>
          <select
            value={data.taxState}
            onChange={(e) => onChange({ ...data, taxState: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
          >
            <option value="">Select State</option>
            {US_STATES.map((state) => (
              <option key={state.value} value={state.value}>
                {state.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isMarriedStatus(data.filingStatus) &&
        renderMemberSection("secondMember", "Spouse Information")}
    </div>
  );
};

export default HouseholdInfoForm;
